import { useMemo } from 'react';
import React from 'react';

type Props = {
  text: string;
  search: string;
  searchTerms?: string[];
};

const getMatchedIndices = (text: string, search: string) => {
  if (search === '') {
    return {
      matchStartIndex: -1,
      matchEndIndex: -1,
    };
  }

  const normalizedText = text.toLocaleLowerCase();
  const textMatchStartIndex = normalizedText.indexOf(search);
  const searchMatchStartIndex = search.indexOf(normalizedText);

  const matchStartIndex = textMatchStartIndex === -1 ? searchMatchStartIndex : textMatchStartIndex;
  const matchEndIndex = matchStartIndex + Math.min(normalizedText.length, search.length);

  return {
    matchStartIndex,
    matchEndIndex,
  };
};

const HighlightedText = ({ text, search, searchTerms = search.trim().toLocaleLowerCase().split(/[\s]+/) }: Props) => {
  const searchTerm = useMemo(() => searchTerms[0] ?? '', [searchTerms]);
  const { matchEndIndex, matchStartIndex } = useMemo(() => getMatchedIndices(text, searchTerm), [searchTerm, text]);
  const startingText = useMemo(() => text.slice(0, matchStartIndex), [text, matchStartIndex]);
  const highlightedText = useMemo(
    () => text.slice(matchStartIndex, matchEndIndex),
    [text, matchStartIndex, matchEndIndex],
  );
  const remainingText = useMemo(() => text.slice(matchEndIndex), [text, matchEndIndex]);

  if (text.length === 0 || searchTerm.length === 0) {
    return <>{text}</>;
  }

  if (matchStartIndex === -1) {
    return <HighlightedText text={text} search={search} searchTerms={searchTerms.slice(1)} />;
  }

  return (
    <>
      {matchStartIndex !== 0 && (
        <HighlightedText text={startingText} search={search} searchTerms={searchTerms.slice(1)} />
      )}
      <strong>{highlightedText}</strong>
      <HighlightedText text={remainingText} search={search} searchTerms={searchTerms} />
    </>
  );
};

export default React.memo(HighlightedText);
